import { supabase, isSupabaseConfigured } from '../lib/supabase';
import { courseNameCandidates } from './courseConfigurationService';

export const COURSE_EMAIL_DELIVERY_MODES = [
  { value: 'in_class', label: 'In class' },
  { value: 'online', label: 'Online' },
  { value: 'blended', label: 'Blended' },
  { value: 'on_site', label: 'On site' },
] as const;

export type CourseEmailDeliveryMode = (typeof COURSE_EMAIL_DELIVERY_MODES)[number]['value'];

export const COURSE_EMAIL_DELIVERY_MODE_VALUES: CourseEmailDeliveryMode[] = COURSE_EMAIL_DELIVERY_MODES.map(
  (m) => m.value,
);

export interface CourseEmailContentBlock {
  id: string;
  name: string;
  htmlContent: string;
  updatedAt: string;
  courseCount: number;
}

export interface CourseEmailContentAssignment {
  courseName: string;
  deliveryMode: CourseEmailDeliveryMode;
}

function mapBlock(row: {
  id: string;
  name: string;
  html_content: string;
  updated_at: string;
}, courseCount = 0): CourseEmailContentBlock {
  return {
    id: row.id,
    name: row.name,
    htmlContent: row.html_content || '',
    updatedAt: row.updated_at,
    courseCount,
  };
}

function distinctCourseCount(rows: Array<{ course_name?: string | null }>): number {
  return new Set(rows.map((r) => String(r.course_name || '').trim()).filter(Boolean)).size;
}

export function normalizeDeliveryMode(raw?: string | null): CourseEmailDeliveryMode | null {
  const v = String(raw || '')
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, '_');
  if (!v) return null;
  if (v === 'in_class' || v === 'inclass') return 'in_class';
  if (v === 'online') return 'online';
  if (v === 'blended') return 'blended';
  if (v === 'on_site' || v === 'onsite') return 'on_site';
  return null;
}

export function deliveryModeLabel(mode: string | null | undefined): string {
  const normalized = normalizeDeliveryMode(mode);
  return COURSE_EMAIL_DELIVERY_MODES.find((m) => m.value === normalized)?.label || mode || '';
}

export const courseEmailContentService = {
  async listBlocks(): Promise<CourseEmailContentBlock[]> {
    if (!isSupabaseConfigured()) return [];
    const { data: blocks, error } = await supabase
      .from('course_email_content_blocks')
      .select('id, name, html_content, updated_at')
      .order('name');
    if (error) {
      console.error('course_email_content_blocks listBlocks:', error);
      return [];
    }
    const { data: assignments } = await supabase
      .from('course_email_content_assignments')
      .select('block_id, course_name');
    const counts = new Map<string, Set<string>>();
    for (const row of assignments || []) {
      const id = String(row.block_id);
      const name = String(row.course_name || '').trim();
      if (!name) continue;
      const set = counts.get(id) || new Set<string>();
      set.add(name);
      counts.set(id, set);
    }
    return (blocks || []).map((b) => mapBlock(b as any, counts.get(b.id)?.size || 0));
  },

  async upsertBlock(input: {
    id?: string;
    name: string;
    htmlContent: string;
  }): Promise<CourseEmailContentBlock> {
    if (!isSupabaseConfigured()) throw new Error('Database is not configured');
    const name = input.name.trim();
    if (!name) throw new Error('Block name is required');
    const payload = {
      name,
      html_content: input.htmlContent,
      updated_at: new Date().toISOString(),
    };
    if (input.id) {
      const { data, error } = await supabase
        .from('course_email_content_blocks')
        .update(payload)
        .eq('id', input.id)
        .select('id, name, html_content, updated_at')
        .single();
      if (error) throw error;
      const { data: assigned } = await supabase
        .from('course_email_content_assignments')
        .select('course_name')
        .eq('block_id', input.id);
      return mapBlock(data as any, distinctCourseCount(assigned || []));
    }
    const { data, error } = await supabase
      .from('course_email_content_blocks')
      .insert(payload)
      .select('id, name, html_content, updated_at')
      .single();
    if (error) throw error;
    return mapBlock(data as any, 0);
  },

  async deleteBlock(id: string): Promise<void> {
    if (!isSupabaseConfigured()) throw new Error('Database is not configured');
    const { error } = await supabase.from('course_email_content_blocks').delete().eq('id', id);
    if (error) throw error;
  },

  async getAssignmentsForBlock(blockId: string): Promise<CourseEmailContentAssignment[]> {
    if (!isSupabaseConfigured()) return [];
    const { data, error } = await supabase
      .from('course_email_content_assignments')
      .select('course_name, delivery_mode')
      .eq('block_id', blockId)
      .order('course_name');
    if (error) {
      console.error('getAssignmentsForBlock:', error);
      return [];
    }
    return (data || [])
      .map((r) => {
        const deliveryMode = normalizeDeliveryMode(r.delivery_mode);
        if (!deliveryMode) return null;
        return {
          courseName: String(r.course_name),
          deliveryMode,
        };
      })
      .filter((row): row is CourseEmailContentAssignment => Boolean(row));
  },

  async setBlockCourses(
    blockId: string,
    courseNames: string[],
    deliveryModes: Array<CourseEmailDeliveryMode | string>,
  ): Promise<CourseEmailContentAssignment[]> {
    if (!isSupabaseConfigured()) throw new Error('Database is not configured');
    const names = [...new Set(courseNames.map((n) => n.trim()).filter(Boolean))];
    const modes = [
      ...new Set(
        deliveryModes
          .map((m) => normalizeDeliveryMode(m))
          .filter((m): m is CourseEmailDeliveryMode => Boolean(m)),
      ),
    ];
    const now = new Date().toISOString();

    const { error: clearOwnError } = await supabase
      .from('course_email_content_assignments')
      .delete()
      .eq('block_id', blockId);
    if (clearOwnError) throw clearOwnError;

    if (names.length === 0) return [];
    if (modes.length === 0) throw new Error('Select at least one delivery mode');

    const { error: stealError } = await supabase
      .from('course_email_content_assignments')
      .delete()
      .in('course_name', names)
      .in('delivery_mode', modes);
    if (stealError) throw stealError;

    const rows = names.flatMap((course_name) =>
      modes.map((delivery_mode) => ({
        course_name,
        delivery_mode,
        block_id: blockId,
        updated_at: now,
      })),
    );
    const { error: insertError } = await supabase.from('course_email_content_assignments').insert(rows);
    if (insertError) throw insertError;

    return names.flatMap((courseName) => modes.map((deliveryMode) => ({ courseName, deliveryMode })));
  },

  async getBlockForCourse(
    courseName: string,
    deliveryMode?: string | null,
  ): Promise<CourseEmailContentBlock | null> {
    if (!isSupabaseConfigured()) return null;
    const candidates = courseNameCandidates(courseName);
    const { data: assignments, error } = await supabase
      .from('course_email_content_assignments')
      .select('course_name, block_id, delivery_mode')
      .in('course_name', candidates)
      .limit(40);
    if (error || !assignments?.length) return null;
    const trimmed = courseName.trim();
    const mode = normalizeDeliveryMode(deliveryMode);
    const exactName = assignments.filter((a) => a.course_name === trimmed);
    const pool = exactName.length ? exactName : assignments;
    const exactMode = mode ? pool.find((a) => a.delivery_mode === mode) : undefined;
    const blockId = (exactMode || pool[0]).block_id;
    const { data: block } = await supabase
      .from('course_email_content_blocks')
      .select('id, name, html_content, updated_at')
      .eq('id', blockId)
      .maybeSingle();
    if (!block) return null;
    return mapBlock(block as any);
  },

  async getCourseEmailContent(courseName: string, deliveryMode?: string | null): Promise<string> {
    const block = await this.getBlockForCourse(courseName, deliveryMode);
    return block?.htmlContent || '';
  },

  async renameCourseAssignment(oldName: string, newName: string): Promise<void> {
    if (!isSupabaseConfigured()) return;
    const fromName = oldName.trim();
    const toName = newName.trim();
    if (!fromName || !toName || fromName === toName) return;
    const { data } = await supabase
      .from('course_email_content_assignments')
      .select('block_id, delivery_mode')
      .eq('course_name', fromName);
    if (!data?.length) return;
    await supabase.from('course_email_content_assignments').delete().eq('course_name', fromName);
    await supabase.from('course_email_content_assignments').upsert(
      data.map((row) => ({
        course_name: toName,
        block_id: row.block_id,
        delivery_mode: row.delivery_mode,
        updated_at: new Date().toISOString(),
      })),
      { onConflict: 'course_name,delivery_mode' },
    );
  },
};
